import React, { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import { faCalendarAlt, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import moment from 'moment';
import { Button, Form } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { getDefaultDashboardDateRange } from './adminDashboardUtils';

const PRESETS = [
  {
    id: 'current',
    label: 'Current month',
    getRange: () => ({
      start: moment().startOf('month').toDate(),
      end: moment().endOf('day').toDate()
    })
  },
  {
    id: '7',
    label: 'Last 7 days',
    getRange: () => ({
      start: moment().subtract(7, 'days').startOf('day').toDate(),
      end: moment().endOf('day').toDate()
    })
  },
  {
    id: '30',
    label: 'Last 30 days',
    getRange: () => ({
      start: moment().subtract(30, 'days').startOf('day').toDate(),
      end: moment().endOf('day').toDate()
    })
  }
];

const formatRangeDisplay = (start, end) => {
  if (!start) return '';
  if (!end) return moment(start).format('MM/DD/YYYY');
  return `${moment(start).format('MM/DD/YYYY')} - ${moment(end).format(
    'MM/DD/YYYY'
  )}`;
};

const AdminDashboardRangeInput = forwardRef(
  ({ value, onClick, startDate, endDate }, ref) => (
    <div className="position-relative" style={{ width: '100%' }}>
      <Form.Control
        size="sm"
        ref={ref}
        onClick={onClick}
        value={value || formatRangeDisplay(startDate, endDate)}
        readOnly
        className="ps-4"
      />
      <FontAwesomeIcon
        icon={faCalendarAlt}
        className="text-primary position-absolute top-50 translate-middle-y ms-2"
      />
    </div>
  )
);

AdminDashboardRangeInput.displayName = 'AdminDashboardRangeInput';
AdminDashboardRangeInput.propTypes = {
  value: PropTypes.string,
  onClick: PropTypes.func,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date)
};

const AdminDashboardDateRange = ({ value, onChange }) => {
  const [customRangeMode, setCustomRangeMode] = useState(false);
  const [preferredPreset, setPreferredPreset] = useState('30');
  const selectedRange = value || getDefaultDashboardDateRange();
  const matchesRange = preset => {
    const range = preset.getRange();
    return (
      moment(selectedRange.start).isSame(range.start, 'day') &&
      moment(selectedRange.end).isSame(range.end, 'day')
    );
  };
  const activePreset =
    preferredPreset === 'custom'
      ? 'custom'
      : PRESETS.find(
          preset => preset.id === preferredPreset && matchesRange(preset)
        )?.id ||
        PRESETS.find(matchesRange)?.id ||
        'custom';
  const [rangeStart, setRangeStart] = useState(
    () => (value || getDefaultDashboardDateRange()).start
  );
  const [rangeEnd, setRangeEnd] = useState(
    () => (value || getDefaultDashboardDateRange()).end
  );

  const applyPreset = preset => {
    const range = preset.getRange();
    setPreferredPreset(preset.id);
    setCustomRangeMode(false);
    setRangeStart(range.start);
    setRangeEnd(range.end);
    onChange(range);
  };

  const handleRangeChange = dates => {
    const [start, end] = dates;
    setRangeStart(start);
    setRangeEnd(end);

    if (start && end) {
      const appliedRange = {
        start,
        end: moment(end).endOf('day').toDate()
      };
      setPreferredPreset('custom');
      onChange(appliedRange);
    }
  };

  const openCustomRange = () => {
    const current = value || getDefaultDashboardDateRange();
    setRangeStart(current.start);
    setRangeEnd(current.end);
    setCustomRangeMode(true);
  };

  return !customRangeMode ? (
    <Flex alignItems="center" className="flex-wrap gap-1">
      {PRESETS.map(preset => (
        <Button
          key={preset.id}
          size="sm"
          className="d-flex align-items-center p-0 me-2 shadow-none"
          variant="default"
          onClick={() => applyPreset(preset)}
        >
          <FontAwesomeIcon
            icon="circle"
            className={`${
              activePreset === preset.id ? 'text-primary' : 'text-300'
            } fs--2 me-1`}
          />
          {preset.label}
        </Button>
      ))}
      <Button
        size="sm"
        className="d-flex align-items-center p-0 shadow-none"
        variant="default"
        onClick={openCustomRange}
        aria-label="Custom date range"
      >
        <FontAwesomeIcon icon={faCalendarAlt} />
      </Button>
    </Flex>
  ) : (
    <Flex alignItems="center" className="gap-1" style={{ maxWidth: '300px' }}>
      <DatePicker
        selectsRange
        startDate={rangeStart}
        endDate={rangeEnd}
        selected={rangeStart}
        onChange={handleRangeChange}
        maxDate={new Date()}
        monthsShown={2}
        dateFormat="MM/dd/yyyy"
        customInput={
          <AdminDashboardRangeInput startDate={rangeStart} endDate={rangeEnd} />
        }
      />
      <Button
        size="sm"
        variant="outline-danger"
        onClick={() => setCustomRangeMode(false)}
        aria-label="Close custom date range"
      >
        <FontAwesomeIcon icon={faTimes} />
      </Button>
    </Flex>
  );
};

AdminDashboardDateRange.propTypes = {
  value: PropTypes.shape({
    start: PropTypes.instanceOf(Date),
    end: PropTypes.instanceOf(Date)
  }),
  onChange: PropTypes.func.isRequired
};

export default AdminDashboardDateRange;
